import { useMemo, type KeyboardEvent } from 'react';
import { formatRelativeTime } from '../../lib/relativeTime';
import type {
  DocumentLifecycle,
  DocumentRow,
  DocumentsGroupBy
} from '../../lib/models/documents';
import type { DocumentSystemRowValues } from '../../lib/vm/useDocumentsListVM';

export interface GalleryCardOption {
  /** Display label for the chip in the Properties picker. */
  label: string;
  /** Stable key — matches the `?cols=` URL value. */
  key: string;
}

export const GALLERY_CHIP_KEYS = [
  'type',
  'lifecycle',
  'owner',
  'last_event_at',
  'body_preview'
] as const;

export type GalleryChipKey = (typeof GALLERY_CHIP_KEYS)[number];

export const GALLERY_CHIP_LABELS: Readonly<Record<GalleryChipKey, string>> = {
  type: 'Type',
  lifecycle: 'Lifecycle',
  owner: 'Owner',
  last_event_at: 'Updated',
  body_preview: 'Body preview'
};

export const GALLERY_DEFAULT_CHIPS: ReadonlyArray<GalleryChipKey> = [
  'type',
  'lifecycle',
  'owner',
  'last_event_at',
  'body_preview'
];

const LIFECYCLE_LABELS: Readonly<Record<DocumentLifecycle, string>> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived'
};

export interface DocumentsGalleryProps {
  rows: ReadonlyArray<DocumentRow>;
  /** Subset of chip keys to render on each card. */
  visibleChips: ReadonlyArray<string>;
  /** Section grouping, or 'none' for a flat grid. */
  groupBy: DocumentsGroupBy;
  /** Selected row ids for multi-select. */
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (id: string, shiftKey: boolean) => void;
  onRowOpen: (id: string) => void;
  /** Lookup tables for resolving id → label. */
  typeLookup: ReadonlyMap<string, string>;
  ownerLookup: ReadonlyMap<string, string>;
  /** Per-doc resolved Owner / Lifecycle / Subtype, sourced from
   *  `custom_attribute_values` via the workspace-pool system defs. */
  systemValuesByDoc: ReadonlyMap<string, DocumentSystemRowValues>;
}

interface GroupedRows {
  key: string;
  label: string;
  rows: ReadonlyArray<DocumentRow>;
}

function groupRows(
  rows: ReadonlyArray<DocumentRow>,
  groupBy: DocumentsGroupBy,
  typeLookup: ReadonlyMap<string, string>,
  ownerLookup: ReadonlyMap<string, string>,
  systemValuesByDoc: ReadonlyMap<string, DocumentSystemRowValues>
): GroupedRows[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: '', rows }];
  }
  const groups = new Map<string, { label: string; rows: DocumentRow[] }>();
  for (const r of rows) {
    let key: string;
    let label: string;
    const sys = systemValuesByDoc.get(r.id);
    switch (groupBy) {
      case 'type':
        key = r.type_id ?? 'untyped';
        label = typeLookup.get(r.type_id ?? '') ?? 'Untyped';
        break;
      case 'lifecycle': {
        const lc = sys?.lifecycle;
        key = lc ?? 'unset';
        label = lc ? LIFECYCLE_LABELS[lc] : 'No lifecycle';
        break;
      }
      case 'owner': {
        const oid = sys?.ownerId ?? null;
        key = oid ?? 'unassigned';
        label = oid
          ? ownerLookup.get(oid) ?? 'Unknown'
          : 'Unassigned';
        break;
      }
      default:
        key = 'all';
        label = '';
    }
    let entry = groups.get(key);
    if (!entry) {
      entry = { label, rows: [] };
      groups.set(key, entry);
    }
    entry.rows.push(r);
  }
  return Array.from(groups.entries())
    .map(([key, v]) => ({ key, label: v.label, rows: v.rows }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Gallery view for `/documents`. Renders a responsive card grid; each card
 * shows the document title plus a subset of chips chosen by the Properties
 * picker (Type / Lifecycle / Owner / Updated / Body preview).
 *
 * Multi-select via per-card checkbox uses the same `selectedIds` set the
 * Table view consumes, so the bulk-action bar keeps working across views.
 * Click anywhere on the card opens the editor; the checkbox + meta links
 * stop propagation so they don't double-fire.
 */
export function DocumentsGallery({
  rows,
  visibleChips,
  groupBy,
  selectedIds,
  onToggleSelect,
  onRowOpen,
  typeLookup,
  ownerLookup,
  systemValuesByDoc
}: DocumentsGalleryProps) {
  const groups = useMemo(
    () =>
      groupRows(
        rows,
        groupBy,
        typeLookup,
        ownerLookup,
        systemValuesByDoc
      ),
    [rows, groupBy, typeLookup, ownerLookup, systemValuesByDoc]
  );

  const showChip = (k: GalleryChipKey) => visibleChips.includes(k);

  function onCardKeyDown(e: KeyboardEvent<HTMLDivElement>, id: string) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowOpen(id);
    }
  }

  return (
    <div className="doc-gallery" data-testid="documents-gallery">
      {groups.map((g) => (
        <section
          key={g.key}
          className="doc-gallery-group"
          data-testid={`documents-gallery-group-${g.key}`}
        >
          {groupBy !== 'none' ? (
            <header
              className="doc-gallery-group-head"
              data-testid={`documents-gallery-group-head-${g.key}`}
            >
              {g.label}
              <span className="doc-gallery-group-count">{g.rows.length}</span>
            </header>
          ) : null}
          <div className="doc-gallery-grid">
            {g.rows.map((r) => {
              const checked = selectedIds.has(r.id);
              const sys = systemValuesByDoc.get(r.id);
              const lifecycle = sys?.lifecycle ?? null;
              const ownerId = sys?.ownerId ?? null;
              return (
                <div
                  key={r.id}
                  className="doc-gallery-card"
                  data-testid={`documents-gallery-card-${r.id}`}
                  data-selected={checked ? '1' : '0'}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open document: ${r.title}`}
                  onClick={() => onRowOpen(r.id)}
                  onKeyDown={(e) => onCardKeyDown(e, r.id)}
                >
                  <label
                    className="doc-gallery-card-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        onToggleSelect(r.id, (e.nativeEvent as MouseEvent).shiftKey)
                      }
                      data-testid={`documents-gallery-card-select-${r.id}`}
                      aria-label={`Select ${r.title}`}
                    />
                  </label>

                  {showChip('type') ? (
                    <div className="doc-gallery-card-type">
                      {typeLookup.get(r.type_id ?? '') ?? ''}
                    </div>
                  ) : null}

                  <div className="doc-gallery-card-title">{r.title}</div>

                  {showChip('body_preview') && r.body_preview ? (
                    <p
                      className="doc-gallery-card-preview"
                      data-testid={`documents-gallery-card-preview-${r.id}`}
                    >
                      {r.body_preview}
                    </p>
                  ) : null}

                  <div className="doc-gallery-card-footer">
                    {showChip('lifecycle') && lifecycle ? (
                      <span
                        className="doc-gallery-card-chip"
                        data-lifecycle={lifecycle}
                      >
                        {LIFECYCLE_LABELS[lifecycle]}
                      </span>
                    ) : null}
                    {showChip('owner') ? (
                      <span className="doc-gallery-card-chip">
                        {ownerId ? ownerLookup.get(ownerId) ?? '—' : '—'}
                      </span>
                    ) : null}
                    {showChip('last_event_at') ? (
                      <span className="doc-gallery-card-time">
                        {formatRelativeTime(r.last_event_at)}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
